import { skipToken } from "@reduxjs/toolkit/query";
import { DatePicker, message, notification } from "antd";
import locale from "antd/es/date-picker/locale/ru_RU";
import dayjs from "dayjs";
import { MessagePayload } from "firebase/messaging";
import { useIdleTimerWithStorage } from "hooks/useIdleTimerWithStorage";
import { useSseExamNotification } from "hooks/useSseExamNotification";
import find from "lodash/find";
import { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import {
  useGetV2PersonsByPersonIdNotificationsQuery,
  useLazyGetV2FilesDownloadUrlQuery,
  useLazyGetV2PersonsByIdQuery,
  usePutV2PersonsByPersonIdNotificationsReadMutation,
  usePutV2PersonsByPersonIdNotificationsTokenMutation,
} from "app/apiRTK";
import { getFirebaseToken, onForegroundMessage } from "app/firebase";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { EnumRoles, FileTypeEnum, INotification, TypeNewExam } from "app/types";

import {
  ArrowLeftIcon,
  AvatarIcon,
  BellHasNotificationIcon,
  BellIcon,
  CalendarIcon,
} from "assets/icons";
import { MessageNotificationIcon } from "assets/icons/MessageNotification";
import { PhoneIcon } from "assets/icons/Phone";

import AntAvatar from "components/AntAvatar";
import createConfig from "components/AntMessageConfig";
import AntPopover from "components/AntPopover";
import Preloader from "components/AntPreloader";
import { MinimalSoundIndicator } from "components/MinimalSoundIndicator";

import { appPreloaderStatusState, setAppError } from "storeSlices/appSettings";
import {
  isOpenApprovePhotoModalState,
  personIdThatNeedApprovePhotoState,
} from "storeSlices/approvePhotoData";
import {
  authPersonIdState,
  authPersonState,
  authRoleState,
} from "storeSlices/login";
import { setHasNewNotification } from "storeSlices/notification";

import { getTitleFromPath } from "utils/pathMapFuncs";

import style from "./Root.module.scss";
import ApprovePhotoModal from "./components/ApprovePhotoModal";
import LeftSideBar from "./components/LeftSideBar";
import NotificationPopup from "./components/NotificationPopup";
import ProfilePopup from "./components/ProfilePopup";
import SupportPopup from "./components/SupportPopup";

const reloadCounter = localStorage.getItem("reloadCounter") || 0;

if (reloadCounter) {
  localStorage.setItem("reloadCounter", reloadCounter);
} else {
  localStorage.setItem("reloadCounter", JSON.stringify(reloadCounter));
}

function Root() {
  const [isProfilePopup, setIsProfilePopup] = useState(false);
  const [isNotificationPopup, setIsNotificationPopup] = useState(false);
  const [isDatePicker, setIsDatePicker] = useState(false);

  const preloaderStatus = useAppSelector(appPreloaderStatusState);
  const isOpenApprovePhotoModal = useAppSelector(isOpenApprovePhotoModalState);
  const personIdThatNeedApprovePhoto = useAppSelector(
    personIdThatNeedApprovePhotoState
  );
  const authPerson = useAppSelector(authPersonState);
  const authPersonId = useAppSelector(authPersonIdState);
  const authRole = useAppSelector(authRoleState);

  const [messageApi, contextHolder] = message.useMessage();
  const [api, contextHolderNotification] = notification.useNotification();

  const location = useLocation();
  const isGisIntegrationPage = location.pathname.includes(
    "/integration/gis/patients"
  );
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { examId, personId, terminalId, medOrgId, organizationId } =
    useParams();

  // Глобальное SSE-подключение для уведомлений о новых осмотрах
  useSseExamNotification(api);

  // Ставим таймер на бездействие
  // useIdleTimerWithStorage();

  // Название страницы в шапке. Может быть с кнопкой Назад
  let pageTitle = getTitleFromPath(location.pathname) || "Главная";

  const requestParams =
    authPersonId && typeof authPersonId === "number"
      ? {
          personId: authPersonId,
          isRead: false,
          page: 1,
          offset: 5,
          sort: "-time",
        }
      : skipToken;

  // useGet
  const {
    data: notifications,
    isError: isNotificationError,
    refetch: notificationsRefetch,
  } = useGetV2PersonsByPersonIdNotificationsQuery(
    !isGisIntegrationPage ? requestParams : skipToken
  );

  // useGetLazy
  const [getPhotoUrl, { data: photoUrl }] = useLazyGetV2FilesDownloadUrlQuery();
  const [getAuthPerson] = useLazyGetV2PersonsByIdQuery();

  // useMutation
  const [changePersonToken] =
    usePutV2PersonsByPersonIdNotificationsTokenMutation();
  const [PutV2NotificationsReadAllByPersonIdMutation] =
    usePutV2PersonsByPersonIdNotificationsReadMutation();

  const newMessage = useCallback(
    (data: MessagePayload) => {
      const msg =
        data.data?.ExamRelatedNotificationMessage ||
        data.data?.TerminalRelatedNotificationMessage ||
        data.data?.PersonRelatedNotificationMessage;

      if (msg) {
        const parsedMsg = JSON.parse(msg) as TypeNewExam;

        // Если роль врач, то фильтруем уведомления об осмотре, т.к. используется SSE, чтобы не было дублирования
        if (authRole?.includes(EnumRoles.Врач)) {
          if (!parsedMsg.ExamId) {
            api.info({
              message: parsedMsg.Title,
              description: parsedMsg.Body,
              duration: 10,
              placement: "bottomRight",
              icon: <MessageNotificationIcon />,
            });
          }
        } else {
          api.info({
            message: parsedMsg.Title,
            description: parsedMsg.Body,
            duration: 10,
            placement: "bottomRight",
            icon: <MessageNotificationIcon />,
          });
        }

        // Запрашиваем уведомления с бэка повторно, т.к. пришло с файрбеза
        notificationsRefetch();
        // Меняем в Redux флаг, что уведомление пришло
        dispatch(setHasNewNotification(true));
      }
    },
    [api, notificationsRefetch, dispatch, authRole]
  );

  useEffect(() => {
    if (isNotificationError) {
      messageApi.open(
        createConfig({
          type: "error",
          content: "Возникла ошибка при загрузке уведомлений",
        })
      );
    }
  }, [isNotificationError, messageApi]);

  useEffect(() => {
    onForegroundMessage(newMessage);
  }, [newMessage]);

  useEffect(() => {
    if (
      authPerson &&
      authPerson.personPhotos &&
      authPerson.personPhotos.length > 0
    ) {
      const actualPhoto = find(authPerson.personPhotos, { status: 1 });
      if (actualPhoto) {
        getPhotoUrl({
          fileType: FileTypeEnum["Фото профиля физ. лица"],
          key: actualPhoto.storageKey,
        });
      }
    }
  }, [authPerson, getPhotoUrl]);

  useEffect(() => {
    if (authPerson && !isGisIntegrationPage) {
      Notification.requestPermission().then(function (result) {
        if (result === "denied" || result === "default") {
          messageApi.open(
            createConfig({
              type: "error",
              content: "Ошибка! Включите разрешение для уведомлений",
            })
          );
        } else {
          getFirebaseToken()
            .then((firebaseToken) => {
              if (firebaseToken) {
                if (authPerson.notificationToken) {
                  if (authPerson.notificationToken !== firebaseToken) {
                    changePersonToken({
                      personId: authPerson.id,
                      notificationToken: firebaseToken,
                    })
                      .unwrap()
                      .then(() => {
                        getAuthPerson(authPerson.id);
                      });
                  }
                } else {
                  changePersonToken({
                    personId: authPerson.id,
                    notificationToken: firebaseToken,
                  })
                    .unwrap()
                    .then(() => {
                      getAuthPerson(authPerson.id);
                    });
                }
              } else {
                console.error("No Firebase token!");
              }
            })
            .catch((err) => {
              console.error(
                "An error occured while retrieving firebase token. ",
                err
              );

              if (+reloadCounter < 3) {
                localStorage.setItem(
                  "reloadCounter",
                  JSON.stringify(+reloadCounter + 1)
                );
                navigate(0);
              }
            });
        }
      });
    }
  }, [
    authPerson,
    getPhotoUrl,
    changePersonToken,
    getAuthPerson,
    messageApi,
    navigate,
  ]);

  useEffect(() => {
    window.addEventListener("error", (event) => {
      dispatch(setAppError(event.message));
    });

    window.addEventListener(
      "unhandledrejection",
      (event: PromiseRejectionEvent) => {
        dispatch(setAppError(event.reason.message));
      }
    );
  }, [dispatch]);

  // Кнопка Назад на конкретном осмотре
  if (examId || personId || terminalId || medOrgId || organizationId) {
    const pathname = window.location.pathname;

    let parentPath = pathname.substring(0, pathname.lastIndexOf("/"));

    // Если есть переданные фильтры, то добавляем их
    if (location.state && location.state.prevPageFilter) {
      parentPath += "?" + location.state.prevPageFilter;
    }

    pageTitle = (
      <>
        <button onClick={() => navigate(parentPath)} className={style.backBtn}>
          <ArrowLeftIcon />
        </button>
        {pageTitle}
      </>
    );
  }

  const readAllNotifications = () => {
    if (authPersonId && typeof authPersonId === "number") {
      PutV2NotificationsReadAllByPersonIdMutation(authPersonId)
        .unwrap()
        .then(() => {
          notificationsRefetch();
        });
    }
  };

  let newMessages: INotification[] | [] = [];
  if (
    notifications &&
    notifications.models &&
    notifications.models.length > 0
  ) {
    newMessages = notifications.models
      .filter((msg) => !msg.isRead)
      .map((msg) => {
        const parsedMsg = JSON.parse(msg.messageJson) as TypeNewExam;
        return {
          id: msg.id,
          title: parsedMsg.Title,
          description: parsedMsg.Body,
          examId: parsedMsg.ExamId,
          personId: parsedMsg.PersonId,
          ownPersonId: msg.personId,
          time: dayjs(msg.creationDate).format("HH:mm"),
          type: msg.type,
        } as INotification;
      });
  }

  return (
    <>
      {contextHolder}
      {contextHolderNotification}
      <Preloader spinning={preloaderStatus} size="large">
        <div
          className={`${style.dashboard} ${
            isGisIntegrationPage ? style.dashboardFullWidth : ""
          }`}
        >
          <div className={style.topNav}>
            <div className={style.pageTitle}>
              <h1>{pageTitle}</h1>
            </div>
            <div className={style.info}>
              <div className={style.calendar}>
                <CalendarIcon />
                <DatePicker
                  value={dayjs(dayjs(), "DD/MM/YYYY")}
                  format={"DD/MM/YYYY"}
                  className={style.datePicker}
                  popupClassName={style.popupDatePicker}
                  inputReadOnly={true}
                  allowClear={false}
                  variant="borderless"
                  suffixIcon={false}
                  locale={locale}
                  showNow={false}
                  open={isDatePicker}
                  onOpenChange={(visible) => {
                    setIsDatePicker(visible);
                  }}
                />
              </div>
              <div className={style.support}>
                <AntPopover
                  content={<SupportPopup />}
                  trigger="click"
                  placement="rightTop"
                >
                  <button type="button">
                    <PhoneIcon />
                  </button>
                </AntPopover>
              </div>
              {!isGisIntegrationPage && (
                <div className={style.notification}>
                  <AntPopover
                    content={
                      <NotificationPopup
                        setIsNotificationPopup={setIsNotificationPopup}
                        notifications={newMessages}
                        countNewMessages={notifications?.totalResults || 0}
                        readAllMessages={readAllNotifications}
                        notificationsRefetch={notificationsRefetch}
                      />
                    }
                    trigger="click"
                    placement="rightTop"
                    onOpenChange={(visible) => setIsNotificationPopup(visible)}
                    open={isNotificationPopup}
                  >
                    <button type="button">
                      {newMessages.length ? (
                        <BellHasNotificationIcon />
                      ) : (
                        <BellIcon />
                      )}
                    </button>
                  </AntPopover>
                </div>
              )}
              <div className={style.profile}>
                <AntPopover
                  content={
                    <ProfilePopup setIsProfilePopup={setIsProfilePopup} />
                  }
                  trigger="click"
                  placement="rightTop"
                  onOpenChange={(visible) => setIsProfilePopup(visible)}
                  open={isProfilePopup}
                >
                  <button type="button">
                    <AntAvatar
                      size={38}
                      icon={<AvatarIcon />}
                      src={photoUrl?.url ? photoUrl.url : undefined}
                    />
                  </button>
                </AntPopover>
              </div>
            </div>
          </div>
          <div className={style.mainPage}>
            {/* Место для отображения текущего роута */}
            <Outlet
              context={{
                rootRefetchNotifications: notificationsRefetch, // для страницы уведомлений
              }}
            />
          </div>
        </div>

        {!isGisIntegrationPage && <LeftSideBar />}
        {/* Делаю проверку тут чтобы модалка не рендерилась раньше времени и дестроилась после закрытия */}
        {isOpenApprovePhotoModal &&
          !!personIdThatNeedApprovePhoto &&
          authRole?.includes(EnumRoles.Руководитель) && (
            <ApprovePhotoModal
              personId={personIdThatNeedApprovePhoto}
              messageApi={messageApi}
              notificationsRefetch={notificationsRefetch}
            />
          )}
        {/* Значок включения звука */}
        {!isGisIntegrationPage && <MinimalSoundIndicator />}
      </Preloader>
    </>
  );
}

export default Root;
